// The learner's shelf: every course they are on, each as a card with its cover,
// how far they have got, and — for a course that is paused or finished — why it
// reads differently from the rest.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { get } from '../api'
import { useFormat, useI18n, useT } from '../i18n'
import common from '../i18n/common'
import drafts from '../i18n/drafts'
import learn from '../i18n/learn'
import { messageOf } from '../lib/ui'
import '../styles/learn.css'
import '../styles/drafts.css'

/** One card of GET /learn/courses: the course row, the learner's standing on it. */
export type CourseCard = {
  id: string
  title: string
  description: string
  /** The cover, an uploaded picture served at /files/{id}. */
  cover_file_id?: string
  gamification_enabled: boolean
  enrollment_status: 'active' | 'paused' | 'finished'
  percent: number
  completed_lectures: number
  /** Published lessons only: what the percentage is measured against. */
  total_lectures: number
  /** Lessons staff have added but not published yet, shown as coming soon. */
  draft_lectures?: number
}

export function Courses() {
  const t = useT(learn)
  const [courses, setCourses] = useState<CourseCard[] | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setFailure(null)
    get<{ items: CourseCard[] }>('/learn/courses', ac.signal)
      .then((res) => setCourses(res.items ?? []))
      .catch((err) => {
        if (!ac.signal.aborted) setFailure({ error: err })
      })
    return () => ac.abort()
  }, [attempt])

  if (failure) return <LoadFailed error={failure.error} onRetry={() => setAttempt((n) => n + 1)} />
  if (!courses) return <Loading />

  if (courses.length === 0) {
    return (
      <div className="empty">
        <h1>{t('noCoursesTitle')}</h1>
        <p>{t('noCoursesHint')}</p>
      </div>
    )
  }

  return (
    <div>
      <h1>{t('myCourses')}</h1>
      <ul className="course-cards">
        {courses.map((c) => (
          <li key={c.id}>
            <CourseTile course={c} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function CourseTile({ course }: { course: CourseCard }) {
  const t = useT(learn)
  const f = useFormat()
  return (
    <Link className="card course-tile" to={`/courses/${course.id}`}>
      <div className="course-tile__media">
        <CourseCover fileId={course.cover_file_id} title={course.title} />
        {course.enrollment_status !== 'active' && (
          <span className="course-tile__state">
            {course.enrollment_status === 'paused' ? t('enrolmentPaused') : t('enrolmentFinished')}
          </span>
        )}
      </div>
      <div className="card__body">
        <h2>{course.title}</h2>
        {course.description.trim() !== '' && <p className="course-tile__about">{course.description}</p>}
        {course.total_lectures === 0 ? (
          <SoonLine />
        ) : (
          <>
            <div
              className="bar"
              role="progressbar"
              aria-label={t('progressOf', { title: course.title })}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={course.percent}
            >
              <div className="bar__fill" style={{ width: `${course.percent}%` }} />
            </div>
            <p className="muted">
              {t('shelfProgress', {
                done: course.completed_lectures,
                total: course.total_lectures,
                percent: f.number(course.percent),
              })}
            </p>
            <MoreSoon count={course.draft_lectures ?? 0} />
          </>
        )}
      </div>
    </Link>
  )
}

/**
 * The foot of a card whose course has no published lesson yet. A bar at 0% and
 * "0/0 lessons" would read as an empty or broken course; the lessons are on
 * their way, and that is what the learner is told.
 */
export function SoonLine() {
  const t = useT(drafts)
  return (
    <p className="course-tile__soon">
      <span className="soon-chip">{t('soon')}</span>
      {t('cardSoon')}
    </p>
  )
}

/** Under a card's progress: how many more lessons are still to be published. */
export function MoreSoon({ count }: { count: number }) {
  const t = useT(drafts)
  if (count <= 0) return null
  return <p className="course-tile__more">{t('cardMoreSoon', { n: count })}</p>
}

/**
 * A course's cover, or — when there is none, or the picture will not load — a
 * green panel with the course's initials. Decorative: the title is always
 * right next to it.
 */
export function CourseCover({ fileId, title }: { fileId?: string | null; title: string }) {
  const [broken, setBroken] = useState(false)
  const { lang } = useI18n()
  useEffect(() => setBroken(false), [fileId])

  if (fileId && !broken) {
    return <img className="cover" src={`/files/${fileId}`} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />
  }
  return (
    <div className="cover cover--blank" data-tone={toneOf(title)} aria-hidden="true">
      <span className="cover__initials">{initials(title, lang)}</span>
    </div>
  )
}

/** Up to two letters: the first of the first two words that have one. */
function initials(title: string, lang: string): string {
  const words = title.match(/[\p{L}\p{N}]+/gu) ?? []
  return (
    words
      .slice(0, 2)
      .map((w) => w.charAt(0))
      .join('')
      .toLocaleUpperCase(lang) || '·'
  )
}

/** One of four shades, fixed per title so a course keeps its colour. */
function toneOf(title: string): number {
  let h = 0
  for (const ch of title) h = (h * 31 + (ch.codePointAt(0) ?? 0)) >>> 0
  return h % 4
}

export function Loading() {
  const t = useT(common)
  return (
    <div className="splash" role="status" aria-label={t('loading')}>
      <div className="splash__pulse" />
    </div>
  )
}

/** A request that failed, kept as the error so its words follow the language on screen. */
export type Failure = { error: unknown }

export function LoadFailed({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const t = useT(common)
  return (
    <div className="empty" role="alert">
      <h1>{t('loadFailed')}</h1>
      <p>{messageOf(error, learn)}</p>
      <button type="button" className="button" onClick={onRetry}>
        {t('retry')}
      </button>
    </div>
  )
}
